import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../../components/ui/hover-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { UserPlus, Copy, Users, Loader2, CheckCircle, Link as LinkIcon, Shield, Building2, Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown, Mail, FileText, UserX, UserCheck, AlertTriangle, RotateCcw, RefreshCw, X, Key, Phone, History, ChevronsUpDown, Check, UserCircle, Briefcase } from "lucide-react";
import RolesManagement from "../../components/RolesManagement";
import UserPermissionsDialog from "../../components/UserPermissionsDialog";
import { useAuth } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// HR Data Hover Card Component
const HRDataHoverCard = ({ user }) => {
  const [hrData, setHrData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHrData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/hr/data/${user.id}`);
      setHrData(response.data);
    } catch (error) {
      setHrData(null);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => {
    // Trigger the parent's openHrDataDialog function
    window.dispatchEvent(new CustomEvent('openHrData', { detail: user }));
  };

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={openDialog}
          onMouseEnter={fetchHrData}
          data-testid={`hr-data-${user.id}`}
          title="HR Data (HR Team Only)"
          className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
        >
          <UserCircle className="h-4 w-4" />
        </Button>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" side="left">
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-teal-900">{user.full_name}</h4>
          {loading ? (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span className="text-xs text-slate-500">Loading...</span>
            </div>
          ) : hrData ? (
            <div className="space-y-1 text-xs">
              {hrData.date_of_joining && (
                <p><strong>DOJ:</strong> {hrData.date_of_joining}</p>
              )}
              {hrData.current_annual_ctc && (
                <p><strong>Current CTC:</strong> {(hrData.currency || 'INR') === 'INR' ? '₹' : '$'}{hrData.current_annual_ctc.toLocaleString()}</p>
              )}
              {hrData.work_location && (
                <p><strong>Location:</strong> {hrData.work_location}</p>
              )}
              {hrData.date_of_birth && (
                <p><strong>DOB:</strong> {hrData.date_of_birth}</p>
              )}
              {hrData.marital_status && (
                <p><strong>Marital:</strong> {hrData.marital_status}</p>
              )}
              {hrData.hometown && (
                <p><strong>Hometown:</strong> {hrData.hometown}</p>
              )}
              {!hrData.date_of_joining && !hrData.current_annual_ctc && !hrData.work_location && (
                <p className="text-slate-400 italic">No HR data available</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400">Click to add HR data</p>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

// Activity Report Components
const DashboardReport = ({ data }) => {
  if (!data || !data.summary) return null;

  const { summary, user, date_range } = data;

  return (
    <div className="space-y-4 print:p-4">
      <div className="bg-slate-50 p-4 rounded-lg print:bg-white">
        <h4 className="font-semibold text-sm text-slate-600 mb-2">Report Summary</h4>
        <p className="text-sm">User: <span className="font-medium">{user?.full_name}</span></p>
        <p className="text-sm">Period: <span className="font-medium">{date_range?.label}</span></p>
        <p className="text-sm">
          {new Date(date_range?.start).toLocaleDateString()} - {new Date(date_range?.end).toLocaleDateString()}
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-blue-700">{summary.total_activities}</p>
            <p className="text-sm text-blue-600 mt-1">Total Activities</p>
          </CardContent>
        </Card>
        
        {Object.entries(summary.by_action || {}).slice(0, 3).map(([action, count]) => (
          <Card key={action} className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6 text-center">
              <p className="text-2xl font-bold text-purple-700">{count}</p>
              <p className="text-xs text-purple-600 mt-1">{action.replace(/_/g, ' ').toUpperCase()}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Activities by Type</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(summary.by_action || {}).map(([action, count]) => (
              <div key={action} className="flex items-center justify-between py-2 border-b last:border-0">
                <span className="text-sm">{action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                <Badge variant="outline" className="bg-purple-50 text-purple-700">{count}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Activity Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(summary.by_date || {}).sort().reverse().map(([date, count]) => (
              <div key={date} className="flex items-center justify-between py-2 border-b last:border-0">
                <span className="text-sm">{new Date(date).toLocaleDateString()}</span>
                <Badge variant="outline" className="bg-blue-50 text-blue-700">{count}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Button onClick={() => window.print()} className="w-full bg-purple-600 hover:bg-purple-700">
        Print Dashboard
      </Button>
    </div>
  );
};

const DetailReport = ({ data }) => {
  if (!data || !data.activities) return null;

  const { activities, user, date_range } = data;

  return (
    <div className="space-y-4 print:p-4">
      <div className="bg-slate-50 p-4 rounded-lg print:bg-white">
        <h4 className="font-semibold text-sm text-slate-600 mb-2">Detailed Activity Report</h4>
        <p className="text-sm">User: <span className="font-medium">{user?.full_name}</span> ({user?.email})</p>
        <p className="text-sm">Period: {new Date(date_range?.start).toLocaleDateString()} - {new Date(date_range?.end).toLocaleDateString()}</p>
        <p className="text-sm">Total Activities: <span className="font-medium">{data.total_activities}</span></p>
      </div>

      {activities.length === 0 ? (
        <p className="text-center text-slate-500 py-8">No activities found for this period</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-3 font-semibold">Date & Time</th>
                <th className="text-left p-3 font-semibold">Action</th>
                <th className="text-left p-3 font-semibold">Entity</th>
                <th className="text-left p-3 font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((activity, idx) => (
                <tr key={activity.id || idx} className="border-t hover:bg-slate-50">
                  <td className="p-3">
                    {new Date(activity.created_at).toLocaleString()}
                  </td>
                  <td className="p-3">
                    <Badge className={
                      activity.action?.includes('created') ? 'bg-green-100 text-green-700' :
                      activity.action?.includes('updated') ? 'bg-blue-100 text-blue-700' :
                      activity.action?.includes('deleted') ? 'bg-red-100 text-red-700' :
                      activity.action?.includes('email') ? 'bg-purple-100 text-purple-700' :
                      activity.action?.includes('sms') ? 'bg-cyan-100 text-cyan-700' :
                      activity.action?.includes('call') ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-100 text-slate-700'
                    }>
                      {activity.action?.replace(/_/g, ' ').toUpperCase()}
                    </Badge>
                  </td>
                  <td className="p-3 capitalize">{activity.entity_type || '-'}</td>
                  <td className="p-3 text-xs text-slate-600">
                    {activity.changes ? JSON.stringify(activity.changes).substring(0, 100) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Button onClick={() => window.print()} className="w-full">
        Print Detail Report
      </Button>
    </div>
  );
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [intendedEmail, setIntendedEmail] = useState("");
  const [isReusable, setIsReusable] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newInviteUrl, setNewInviteUrl] = useState("");
  
  // Cache reference to avoid hitting database on every page visit
  const cacheRef = useRef({
    users: null,
    teams: null,
    invites: null,
    forms: null,
    timestamp: null
  });
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterExempt, setFilterExempt] = useState("all");
  const [filterTeam, setFilterTeam] = useState("all");
  const [filterManager, setFilterManager] = useState("all");
  const [filterNoTeams, setFilterNoTeams] = useState(false);
  const [filterTelnyxOnly, setFilterTelnyxOnly] = useState(false);
  const [filterExcludeExempt, setFilterExcludeExempt] = useState(false);
  const [sortColumn, setSortColumn] = useState("full_name");
  const [sortDirection, setSortDirection] = useState("asc");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Edit user state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedReportsTo, setSelectedReportsTo] = useState("");
  const [reportsToSearchOpen, setReportsToSearchOpen] = useState(false);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [teamRoles, setTeamRoles] = useState({}); // { teamId: 'USER' | 'TEAM_LEADER' }
  const [selectedExempt, setSelectedExempt] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Forms state
  const [forms, setForms] = useState([]);
  
  // Telnyx status for all users
  const [telnyxStatusMap, setTelnyxStatusMap] = useState({});
  
  // Report dialog state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportUser, setReportUser] = useState(null);
  const [reportDays, setReportDays] = useState("30");
  const [reportFormat, setReportFormat] = useState("column");
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  
  // Permissions dialog state
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState(null);

  // Telnyx setup dialog state
  const [telnyxDialogOpen, setTelnyxDialogOpen] = useState(false);
  const [telnyxUser, setTelnyxUser] = useState(null);
  const [telnyxSettings, setTelnyxSettings] = useState({
    phone_number: '',
    connection_id: '',
    messaging_profile_id: '',
    webrtc_username: '',
    webrtc_password: ''
  });
  const [telnyxLoading, setTelnyxLoading] = useState(false);
  const [telnyxSaving, setTelnyxSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResults, setConnectionTestResults] = useState(null);
  
  // Test Call/SMS state
  const [testCallNumber, setTestCallNumber] = useState('');
  const [testSmsNumber, setTestSmsNumber] = useState('');
  const [testSmsMessage, setTestSmsMessage] = useState('');
  const [testingCall, setTestingCall] = useState(false);
  const [testingSms, setTestingSms] = useState(false);


  // HR Data state
  const [hrDataDialogOpen, setHrDataDialogOpen] = useState(false);
  const [hrDataUser, setHrDataUser] = useState(null);
  const [hrData, setHrData] = useState({});
  const [loadingHrData, setLoadingHrData] = useState(false);
  const [savingHrData, setSavingHrData] = useState(false);
  const [isHrTeamMember, setIsHrTeamMember] = useState(false);


  // Bulk selection state
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  
  // Email merge dialog state
  const [emailMergeDialogOpen, setEmailMergeDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmails, setSendingEmails] = useState(false);
  
  // Login history dialog state
  const [loginHistoryDialogOpen, setLoginHistoryDialogOpen] = useState(false);
  const [loginHistoryUser, setLoginHistoryUser] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);

  // Activity Report state
  const [activityReportDialogOpen, setActivityReportDialogOpen] = useState(false);
  const [activityReportUser, setActivityReportUser] = useState(null);
  const [activityDateRange, setActivityDateRange] = useState('today');
  const [activityCustomStart, setActivityCustomStart] = useState('');
  const [activityCustomEnd, setActivityCustomEnd] = useState('');
  const [activityTypes, setActivityTypes] = useState(['all']);
  const [activityReportType, setActivityReportType] = useState('dashboard');
  const [activityReportData, setActivityReportData] = useState(null);
  const [loadingActivityReport, setLoadingActivityReport] = useState(false);

  const [loadingHistory, setLoadingHistory] = useState(false);

  // Toggle single user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Deselect all users
  const deselectAllUsers = () => {
    setSelectedUserIds(new Set());
  };

  // Get selected users data
  const getSelectedUsers = () => {
    return users.filter(u => selectedUserIds.has(u.id));
  };

  // Insert variable into email body
  const insertVariable = (variable) => {
    setEmailBody(prev => prev + `{{${variable}}}`);
  };

  // Send bulk emails with merge
  const handleSendBulkEmails = async () => {
    const selectedUsers = getSelectedUsers();
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    if (!emailSubject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }
    if (!emailBody.trim()) {
      toast.error('Please enter an email body');
      return;
    }

    setSendingEmails(true);
    try {
      const response = await axios.post(`${API}/admin/bulk-email`, {
        user_ids: selectedUsers.map(u => u.id),
        subject: emailSubject,
        body: emailBody
      });
      
      toast.success(`Emails sent to ${response.data.sent_count} users`);
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} emails failed to send`);
      }
      
      setEmailMergeDialogOpen(false);
      setEmailSubject('');
      setEmailBody('');
      setSelectedUserIds(new Set());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingEmails(false);
    }
  };

  const openPermissionsDialog = (user) => {
    setPermissionsUser(user);
    setPermissionsDialogOpen(true);
  };

  const openTelnyxDialog = async (user) => {
    setTelnyxUser(user);
    setTelnyxDialogOpen(true);
    setTelnyxLoading(true);
    // Reset test fields
    setTestCallNumber('');
    setTestSmsNumber('');
    setTestSmsMessage('');
    
    try {
      const response = await axios.get(`${API}/telnyx/admin/settings/${user.id}`);
      setTelnyxSettings({
        phone_number: response.data.phone_number || '',
        connection_id: response.data.connection_id || '',
        messaging_profile_id: response.data.messaging_profile_id || '',
        webrtc_username: response.data.webrtc_username || '',
        webrtc_password: ''
      });
    } catch (error) {
      console.error('Failed to load Telnyx settings:', error);
      setTelnyxSettings({
        phone_number: '',
        connection_id: '',
        messaging_profile_id: '',
        webrtc_username: '',
        webrtc_password: ''
      });
    } finally {
      setTelnyxLoading(false);
    }
  };

  const handleSaveTelnyxSettings = async () => {
    if (!telnyxUser) return;
    
    setTelnyxSaving(true);
    try {
      await axios.put(`${API}/telnyx/admin/settings/${telnyxUser.id}`, telnyxSettings);
      toast.success(`Telnyx settings saved for ${telnyxUser.full_name}`);
      
      // Update local status map
      setTelnyxStatusMap(prev => ({
        ...prev,
        [telnyxUser.id]: {
          configured: !!telnyxSettings.phone_number,
          has_phone: !!telnyxSettings.phone_number,
          has_connection: !!telnyxSettings.connection_id
        }
      }));
      
      // Invalidate cache so next refresh fetches fresh data
      cacheRef.current.timestamp = 0;
      
      setTelnyxDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Telnyx settings');
    } finally {
      setTelnyxSaving(false);
    }
  };

  const openLoginHistoryDialog = async (user) => {
    setLoginHistoryUser(user);
    setLoginHistoryDialogOpen(true);
    setLoadingHistory(true);
    setLoginHistory([]);
    
    try {
      const response = await axios.get(`${API}/auth/login-history/${user.id}`);
      setLoginHistory(response.data);
    } catch (error) {
      console.error('Failed to load login history:', error);
      toast.error('Failed to load login history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleTestCall = async () => {
    if (!testCallNumber.trim()) {
      toast.error('Please enter a phone number to call');
      return;
    }
    if (!telnyxUser) return;
    
    setTestingCall(true);
    try {
      await axios.post(`${API}/telnyx/admin/test-call/${telnyxUser.id}`, {
        to_number: testCallNumber.trim()
      });
      toast.success(`Test call initiated to ${testCallNumber}`);
      setTestCallNumber('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate test call');
    } finally {
      setTestingCall(false);
    }
  };

  const handleTestConnection = async () => {
    if (!telnyxUser) return;
    
    setTestingConnection(true);
    setConnectionTestResults(null);
    try {
      const response = await axios.post(`${API}/telnyx/admin/test-connection/${telnyxUser.id}`);
      setConnectionTestResults(response.data);
      
      if (response.data.success) {
        toast.success(response.data.summary);
      } else {
        toast.error(response.data.summary);
      }
    } catch (error) {
      console.error('Connection test error:', error);
      setConnectionTestResults({
        success: false,
        summary: 'Test failed',
        checks: [{
          name: 'Connection Test',
          status: 'error',
          message: error.response?.data?.detail || 'Failed to test connection'
        }]
      });
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };


  const handleResetUserPassword = async (user) => {
    if (!confirm(`Reset password for ${user.full_name}?\n\nA temporary password will be generated and sent to ${user.email}. The user will be required to change it on their next login.`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/reset-user-password/${user.id}`);
      toast.success(
        <div>
          <p className="font-semibold">Password Reset Successfully</p>
          <p className="text-sm mt-1">Temporary password sent to {user.email}</p>
          <p className="text-xs mt-1 font-mono bg-slate-100 p-1 rounded">Temp: {response.data.temp_password}</p>
        </div>,
        { duration: 8000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const openActivityReportDialog = (user) => {
    setActivityReportUser(user);
    setActivityReportDialogOpen(true);
    setActivityReportData(null);
    setActivityDateRange('today');
    setActivityTypes(['all']);
    setActivityReportType('dashboard');
  };

  const handleGenerateActivityReport = async () => {
    if (!activityReportUser) return;

    setLoadingActivityReport(true);
    try {
      const response = await axios.post(`${API}/activity-logs/user-report`, {
        user_id: activityReportUser.id,
        date_range: activityDateRange,
        start_date: activityCustomStart || null,
        end_date: activityCustomEnd || null,
        activity_types: activityTypes,
        report_type: activityReportType
      });
      
      setActivityReportData(response.data);
      toast.success('Activity report generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate activity report');
    } finally {
      setLoadingActivityReport(false);
    }
  };

  const openHrDataDialog = async (user) => {
    setHrDataUser(user);
    setLoadingHrData(true);
    setHrDataDialogOpen(true);
    
    // Always fetch fresh data when opening dialog to ensure accuracy
    try {
      const response = await axios.get(`${API}/hr/data/${user.id}?_t=${Date.now()}`); // Cache buster
      setHrData(response.data || {});
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied. HR team members only.");
        setHrDataDialogOpen(false);
      } else {
        setHrData({});
      }
    } finally {
      setLoadingHrData(false);
    }
  };

  const handleSaveHrData = async () => {
    if (!hrDataUser) return;

    setSavingHrData(true);
    try {
      await axios.put(`${API}/hr/data/${hrDataUser.id}`, hrData);
      toast.success("HR data saved successfully");
      setHrDataDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save HR data");
    } finally {
      setSavingHrData(false);
    }
  };

  const handleTestSms = async () => {
    if (!testSmsNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }
    if (!testSmsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (!telnyxUser) return;
    
    setTestingSms(true);
    try {
      await axios.post(`${API}/telnyx/admin/test-sms/${telnyxUser.id}`, {
        to_number: testSmsNumber.trim(),
        message: testSmsMessage.trim()
      });
      toast.success(`Test SMS sent to ${testSmsNumber}`);
      setTestSmsNumber('');
      setTestSmsMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setTestingSms(false);
    }
  };

  const openReportDialog = (user) => {
    setReportUser(user);
    setReportDays("30");
    setReportFormat("column");
    setReportDialogOpen(true);
  };

  const generateReport = () => {
    if (reportUser) {
      // Open report in new tab
      const url = `/admin/user-report/${reportUser.id}?days=${reportDays}&format=${reportFormat}`;
      window.open(url, '_blank');
      setReportDialogOpen(false);
    }
  };



  // Listen for HR Data dialog open event from HoverCard
  useEffect(() => {
    const handleOpenHrData = (event) => {
      openHrDataDialog(event.detail);
    };
    
    window.addEventListener('openHrData', handleOpenHrData);
    return () => window.removeEventListener('openHrData', handleOpenHrData);
  }, []);

  useEffect(() => {
    fetchData(false); // Use cache on initial load
  }, []);

  const fetchData = async (forceRefresh = false) => {
    // Check if we have valid cached data
    const now = Date.now();
    const cache = cacheRef.current;
    
    if (!forceRefresh && cache.timestamp && (now - cache.timestamp) < CACHE_DURATION && cache.users) {
      // Use cached data
      setUsers(cache.users);
      setInvites(cache.invites || []);
      setTeams(cache.teams || []);
      setForms(cache.forms || []);
      setTelnyxStatusMap(cache.telnyxStatus || {});
      setLoading(false);
      return;
    }
    
    // Fetch fresh data from database
    if (forceRefresh) {
      setRefreshing(true);
    }
    
    try {
      const [usersRes, invitesRes, teamsRes, formsRes, telnyxStatusRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/invites`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/forms`),
        axios.get(`${API}/telnyx/admin/status/all`).catch(() => ({ data: {} })),
      ]);
      // Sort users alphabetically by full_name
      const sortedUsers = usersRes.data.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      const activeFormsData = formsRes.data.filter(f => f.is_active);
      
      // Update cache
      cacheRef.current = {
        users: sortedUsers,
        invites: invitesRes.data,
        teams: teamsRes.data,
        forms: activeFormsData,
        telnyxStatus: telnyxStatusRes.data || {},
        timestamp: Date.now()
      };
      
      setUsers(sortedUsers);
      setInvites(invitesRes.data);
      setTeams(teamsRes.data);
      setForms(activeFormsData);
      setTelnyxStatusMap(telnyxStatusRes.data || {});
      
      // Check if current user is in HR team
      if (currentUser) {
        const hrTeam = teamsRes.data.find(t => t.name.toLowerCase() === 'hr');
        if (hrTeam) {
          const userTeams = currentUser.team_ids || [];
          setIsHrTeamMember(userTeams.includes(hrTeam.id) || currentUser.role === 'ADMIN');
        }
      }
      
      if (forceRefresh) {
        toast.success("Data refreshed from database");
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }

  };
  
  // Reset filters and show cached data
  const handleReset = () => {
    setSearchQuery("");
    setFilterRole("all");
    setFilterStatus("all");
    setFilterExempt("all");
    setFilterTeam("all");
    setFilterNoTeams(false);
    setFilterExcludeExempt(false);
    setCurrentPage(1);
    toast.success("Filters cleared");
  };
  
  // Force refresh data from database
  const handleRefresh = () => {
    fetchData(true);
  };

  const handleCreateInvite = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/invites/create`, {
        intended_email: isReusable ? null : (intendedEmail || null),
        is_reusable: isReusable,
      });
      setNewInviteUrl(response.data.setup_url);
      toast.success("Invite created successfully!");
      fetchData(true); // Force refresh after creating invite
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invite");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(newInviteUrl);
    toast.success("Link copied to clipboard!");
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      const endpoint = isActive ? "deactivate" : "activate";
      await axios.post(`${API}/users/${userId}/${endpoint}`);
      toast.success(`User ${isActive ? "deactivated" : "activated"}`);
      fetchData(true); // Force refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/invites/${inviteId}/revoke`);
      toast.success("Invite revoked");
      fetchData(true); // Force refresh
    } catch (error) {
      toast.error("Failed to revoke invite");
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/invites/${inviteId}`);
      toast.success("Invite deleted");
      fetchData(true); // Force refresh
    } catch (error) {
      toast.error("Failed to delete invite");
    }
  };

  const handleCopyInviteLink = async (setupUrl) => {
    if (setupUrl) {
      try {
        await navigator.clipboard.writeText(setupUrl);
        toast.success("Link copied to clipboard!");
      } catch (err) {
        // Fallback for browsers that don't support clipboard API
        const textArea = document.createElement("textarea");
        textArea.value = setupUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("Link copied to clipboard!");
      }
    } else {
      toast.error("Link not available - this invite was created before this feature");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("User deleted successfully");
      fetchData(true); // Force refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.role);
    setSelectedReportsTo(user.reports_to || "");
    setSelectedTeamIds(user.team_ids || []);
    setSelectedExempt(user.is_exempt || false);
    setSelectedFormId(user.assigned_form_id || "");
    
    // Initialize team roles from user data or default to USER
    const initialTeamRoles = {};
    (user.team_ids || []).forEach(teamId => {
      // Check if user has team_roles data, otherwise use global role or default
      const teamRole = user.team_roles?.[teamId] || (user.role === 'TEAM_LEADER' ? 'TEAM_LEADER' : 'USER');
      initialTeamRoles[teamId] = teamRole;
    });
    setTeamRoles(initialTeamRoles);
    
    setEditDialogOpen(true);
  };

  const handleTeamToggle = (teamId) => {
    setSelectedTeamIds(prev => {
      if (prev.includes(teamId)) {
        // Remove team - also remove from teamRoles
        const newRoles = { ...teamRoles };
        delete newRoles[teamId];
        setTeamRoles(newRoles);
        return prev.filter(id => id !== teamId);
      } else {
        // Add team - set default role
        setTeamRoles(prev => ({ ...prev, [teamId]: 'USER' }));
        return [...prev, teamId];
      }
    });
  };

  const handleTeamRoleChange = (teamId, role) => {
    setTeamRoles(prev => ({ ...prev, [teamId]: role }));
  };

  const handleSaveUserChanges = async () => {
    setSaving(true);
    try {
      // Update role if changed (this is the default/global role)
      if (selectedRole !== selectedUser.role) {
        await axios.put(`${API}/users/${selectedUser.id}/role`, { role: selectedRole });
      }
      
      // Update reports_to if changed
      const currentReportsTo = selectedUser.reports_to || "";
      if (selectedReportsTo !== currentReportsTo) {
        await axios.put(`${API}/users/${selectedUser.id}`, { reports_to: selectedReportsTo || null });
      }
      
      // Update teams and team roles if changed
      const currentTeamIds = selectedUser.team_ids || [];
      const teamsChanged = JSON.stringify(selectedTeamIds.sort()) !== JSON.stringify(currentTeamIds.sort());
      const rolesChanged = JSON.stringify(teamRoles) !== JSON.stringify(selectedUser.team_roles || {});
      
      if (teamsChanged || rolesChanged) {
        await axios.put(`${API}/users/${selectedUser.id}/teams`, { 
          team_ids: selectedTeamIds,
          team_roles: teamRoles
        });
      }
      
      // Update exempt status if changed
      const currentExempt = selectedUser.is_exempt || false;
      if (selectedExempt !== currentExempt) {
        await axios.put(`${API}/users/${selectedUser.id}/exempt`, { is_exempt: selectedExempt });
      }
      
      // Update assigned form if changed
      const currentFormId = selectedUser.assigned_form_id || "";
      if (selectedFormId !== currentFormId) {
        await axios.put(`${API}/users/${selectedUser.id}/assign-form?form_id=${selectedFormId || ''}`);
      }
      
      toast.success("User updated successfully!");
      setEditDialogOpen(false);
      fetchData(true); // Force refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  // Deactivate user (disables login but keeps data for reports)
  const handleDeactivateUser = async () => {
    if (!selectedUser) return;
    
    setDeactivating(true);
    try {
      await axios.post(`${API}/users/${selectedUser.id}/deactivate`);
      toast.success(`${selectedUser.full_name} has been deactivated`);
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate user");
    } finally {
      setDeactivating(false);
    }
  };

  // Reactivate user
  const handleReactivateUser = async () => {
    if (!selectedUser) return;
    
    setDeactivating(true);
    try {
      await axios.post(`${API}/users/${selectedUser.id}/reactivate`);
      toast.success(`${selectedUser.full_name} has been reactivated`);
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate user");
    } finally {
      setDeactivating(false);
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = () => {
    setUserToDelete(selectedUser);
    setDeleteDialogOpen(true);
  };

  // Delete user and all their data (from Edit dialog with confirmation)
  const handleConfirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success(`${userToDelete.full_name} and all their data have been deleted`);
      setDeleteDialogOpen(false);
      setEditDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const resetInviteDialog = () => {
    setIntendedEmail("");
    setIsReusable(false);
    setNewInviteUrl("");
    setInviteDialogOpen(false);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "ADMIN": return "bg-purple-100 text-purple-700 border-purple-200";
      case "TEAM_LEADER": return "bg-blue-100 text-blue-700 border-blue-200";
      default: return "";
    }
  };

  // Get unique managers from users (users who have direct reports)
  const managers = useMemo(() => {
    const managerIds = new Set();
    users.forEach(user => {
      if (user.reports_to) {
        managerIds.add(user.reports_to);
      }
    });
    
    // Get manager details
    const managerList = Array.from(managerIds)
      .map(managerId => users.find(u => u.id === managerId))
      .filter(Boolean)
      .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));
    
    return managerList;
  }, [users]);

  // Sort handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Get sort icon
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDirection === "asc" 
      ? <ArrowUp className="h-3 w-3 ml-1" /> 
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Filtered and sorted users
  const filteredUsers = useMemo(() => {
    let result = [...users];
    
    // Search filter (name or email)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(u => 
        u.full_name?.toLowerCase().includes(query) || 
        u.email?.toLowerCase().includes(query)
      );
    }
    
    // Role filter
    if (filterRole !== "all") {
      result = result.filter(u => u.role === filterRole);
    }
    
    // Status filter
    if (filterStatus !== "all") {
      result = result.filter(u => filterStatus === "active" ? u.is_active : !u.is_active);
    }
    
    // Exempt filter
    if (filterExempt !== "all") {
      result = result.filter(u => filterExempt === "yes" ? u.is_exempt : !u.is_exempt);
    }
    
    // Team filter
    if (filterTeam !== "all") {
      result = result.filter(u => u.team_ids?.includes(filterTeam));
    }
    
    // Manager filter
    if (filterManager !== "all") {
      result = result.filter(u => u.reports_to === filterManager);
    }
    
    // No teams filter
    if (filterNoTeams) {
      result = result.filter(u => !u.team_ids || u.team_ids.length === 0);
    }
    
    // Telnyx configured filter
    if (filterTelnyxOnly) {
      result = result.filter(u => telnyxStatusMap[u.id]?.configured);
    }
    
    // Exclude exempt users filter
    if (filterExcludeExempt) {
      result = result.filter(u => !u.is_exempt);
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case "full_name":
          aVal = a.full_name?.toLowerCase() || "";
          bVal = b.full_name?.toLowerCase() || "";
          break;
        case "email":
          aVal = a.email?.toLowerCase() || "";
          bVal = b.email?.toLowerCase() || "";
          break;
        case "role":
          aVal = a.role || "";
          bVal = b.role || "";
          break;
        case "is_active":
          aVal = a.is_active ? 1 : 0;
          bVal = b.is_active ? 1 : 0;
          break;
        case "is_exempt":
          aVal = a.is_exempt ? 1 : 0;
          bVal = b.is_exempt ? 1 : 0;
          break;
        case "created_at":
          aVal = a.created_at || "";
          bVal = b.created_at || "";
          break;
        default:
          aVal = "";
          bVal = "";
      }
      
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [users, searchQuery, filterRole, filterStatus, filterExempt, filterTeam, filterManager, filterNoTeams, filterTelnyxOnly, filterExcludeExempt, telnyxStatusMap, sortColumn, sortDirection]);

  // Select all filtered users (on current page or all)
  const selectAllUsers = () => {
    const allFilteredIds = filteredUsers.map(u => u.id);
    setSelectedUserIds(new Set(allFilteredIds));
  };

  // Check if all filtered users are selected
  const allFilteredSelected = filteredUsers.length > 0 && 
    filteredUsers.every(u => selectedUserIds.has(u.id));

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredUsers.slice(startIndex, startIndex + pageSize);
  }, [filteredUsers, currentPage, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterRole, filterStatus, filterExempt, filterTeam, filterManager, filterNoTeams, filterTelnyxOnly, filterExcludeExempt, pageSize]);

  const activeUsers = users.filter((u) => u.is_active);
  const teamLeaders = users.filter(u => u.role === "TEAM_LEADER");
  const pendingInvites = invites.filter((i) => {
    // For reusable invites, just check expiry
    if (i.is_reusable) {
      return new Date(i.expires_at) > new Date();
    }
    // For single-use invites, check both used_at and expiry
    return !i.used_at && new Date(i.expires_at) > new Date();
  });
  
  // All active (non-expired, non-used) invites for the tab
  const activeInvites = invites.filter((i) => {
    if (i.is_reusable) {
      return new Date(i.expires_at) > new Date();
    }
    return !i.used_at && new Date(i.expires_at) > new Date();
  });

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            User Management
          </h1>
          <p className="text-slate-600 mt-1">
            Manage team members, roles, and invites
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Selection info and email button */}
          {selectedUserIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">
                {selectedUserIds.size} selected
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={deselectAllUsers}
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
              <Button 
                data-testid="email-selected-users"
                onClick={() => setEmailMergeDialogOpen(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Mail className="mr-2 h-4 w-4" />
                Email Selected
              </Button>
            </div>
          )}

          <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-invite-button">
                <UserPlus className="mr-2 h-4 w-4" />
                Create Invite
              </Button>
            </DialogTrigger>
          <DialogContent>
            {newInviteUrl ? (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Invite Created!
                  </DialogTitle>
                  <DialogDescription>
                    Share this link with the user to set up their account.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    <LinkIcon className="h-4 w-4 text-slate-400 shrink-0" />
                    <code className="text-sm break-all flex-1">{newInviteUrl}</code>
                  </div>
                  <Button onClick={handleCopyLink} className="w-full" data-testid="copy-invite-link-button">
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Link
                  </Button>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={resetInviteDialog}>
                    Done
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Create New Invite</DialogTitle>
                  <DialogDescription>
                    Generate a setup link for new team members.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* Reusable Toggle */}
                  <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      id="isReusable"
                      checked={isReusable}
                      onCheckedChange={setIsReusable}
                      data-testid="reusable-checkbox"
                    />
                    <div className="flex-1">
                      <label
                        htmlFor="isReusable"
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        Reusable Link (Multiple Users)
                      </label>
                      <p className="text-xs text-slate-500 mt-1">
                        Create a generic link that multiple team members can use to sign up
                      </p>
                    </div>
                  </div>

                  {/* Email field - only show for single-use invites */}
                  {!isReusable && (
                    <div className="space-y-2">
                      <Label htmlFor="intendedEmail">Intended Email (Optional)</Label>
                      <Input
                        id="intendedEmail"
                        type="email"
                        placeholder="user@company.com"
                        value={intendedEmail}
                        onChange={(e) => setIntendedEmail(e.target.value)}
                        data-testid="invite-email-input"
                      />
                      <p className="text-xs text-slate-500">
                        If specified, only this email can use the invite link.
                      </p>
                    </div>
                  )}

                  {isReusable && (
                    <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <strong>Reusable Link:</strong> Anyone with this link can create an account. 
                        Share it with your team via email, Slack, or any channel.
                      </p>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateInvite} disabled={creating} data-testid="generate-invite-button">
                    {creating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      isReusable ? "Generate Reusable Link" : "Generate Link"
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-sm text-slate-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeUsers.length}</p>
                <p className="text-sm text-slate-500">Active Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Team Leaders with Hover */}
        <HoverCard openDelay={100} closeDelay={100}>
          <HoverCardTrigger asChild>
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Shield className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{teamLeaders.length}</p>
                    <p className="text-sm text-slate-500">Team Leaders</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </HoverCardTrigger>
          <HoverCardContent className="w-80 max-h-[300px] overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
              <Shield className="h-4 w-4 text-blue-600" />
              <span className="font-semibold">Team Leaders ({teamLeaders.length})</span>
            </div>
            {teamLeaders.length > 0 ? (
              <div className="space-y-2">
                {teamLeaders.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg text-sm">
                    <div>
                      <p className="font-medium text-slate-800">{user.full_name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                      {user.team_names && user.team_names.length > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          Teams: {user.team_names.join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-2 text-sm">No team leaders assigned yet</p>
            )}
          </HoverCardContent>
        </HoverCard>

        {/* Pending Invites with Hover */}
        <HoverCard openDelay={100} closeDelay={100}>
          <HoverCardTrigger asChild>
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <LinkIcon className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{pendingInvites.length}</p>
                    <p className="text-sm text-slate-500">Pending Invites</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </HoverCardTrigger>
          <HoverCardContent className="w-80 max-h-[300px] overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
              <LinkIcon className="h-4 w-4 text-amber-600" />
              <span className="font-semibold">Active Invite Links ({pendingInvites.length})</span>
            </div>
            {pendingInvites.length > 0 ? (
              <div className="space-y-2">
                {pendingInvites.map((invite) => (
                  <div key={invite.id} className="p-2 bg-amber-50 rounded-lg text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className={invite.is_reusable ? "bg-blue-50 text-blue-700 border-blue-200 text-xs" : "text-xs"}>
                        {invite.is_reusable ? "Reusable" : "Single-use"}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        Expires {format(parseISO(invite.expires_at), "MMM d")}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600">
                      {invite.is_reusable 
                        ? `${invite.use_count || 0} signups so far` 
                        : (invite.intended_email || "Any email")}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-2 text-sm">No pending invites</p>
            )}
          </HoverCardContent>
        </HoverCard>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <Tabs defaultValue="members" className="w-full">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="members" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Team Members
              <Badge variant="secondary" className="ml-1 text-xs">{users.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="invites" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Active Invites
              <Badge variant="secondary" className="ml-1 text-xs">{activeInvites.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Roles
            </TabsTrigger>
          </TabsList>
          
          {/* Team Members Tab */}
          <TabsContent value="members" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle>Team Members</CardTitle>
                    <CardDescription>
                      {filteredUsers.length} of {users.length} users
                    </CardDescription>
                  </div>
                </div>
                
                {/* Search and Filters */}
                <div className="mt-4 space-y-4">
                  {/* Search with Reset and Refresh icons */}
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-10"
                        data-testid="user-search-input"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          title="Clear search"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleReset}
                      title="Reset all filters (use cached data)"
                      className="shrink-0"
                      data-testid="reset-filters-btn"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleRefresh}
                      disabled={refreshing}
                      title="Refresh data from database"
                      className="shrink-0"
                      data-testid="refresh-data-btn"
                    >
                      {refreshing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  
                  {/* Filters */}
                  <div className="flex flex-wrap gap-2">
                    <Select value={filterRole} onValueChange={setFilterRole}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="ADMIN">Admin</SelectItem>
                        <SelectItem value="TEAM_LEADER">Team Leader</SelectItem>
                        <SelectItem value="USER">User</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-[130px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterExempt} onValueChange={setFilterExempt}>
                      <SelectTrigger className="w-[130px]">
                        <SelectValue placeholder="Exempt" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="yes">Exempt</SelectItem>
                        <SelectItem value="no">Not Exempt</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterTeam} onValueChange={setFilterTeam}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Team" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Teams</SelectItem>
                        {teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterManager} onValueChange={setFilterManager}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Manager" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Managers</SelectItem>
                        {managers.map((manager) => (
                          <SelectItem key={manager.id} value={manager.id}>{manager.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* No Teams Filter Toggle */}
                    <div className="flex items-center space-x-2 px-3 py-2 border rounded-md bg-white">
                      <Checkbox
                        id="filterNoTeams"
                        checked={filterNoTeams}
                        onCheckedChange={setFilterNoTeams}
                        data-testid="filter-no-teams"
                      />
                      <label
                        htmlFor="filterNoTeams"
                        className="text-sm font-medium leading-none cursor-pointer whitespace-nowrap"
                      >
                        No Teams Only
                      </label>
                    </div>
                    
                    {/* Telnyx Filter */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="filterTelnyx"
                        checked={filterTelnyxOnly}
                        onCheckedChange={setFilterTelnyxOnly}
                        data-testid="filter-telnyx"
                      />
                      <label
                        htmlFor="filterTelnyx"
                        className="text-sm font-medium leading-none cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <Phone className="h-3 w-3 text-green-600" />
                        TelNyx
                      </label>
                    </div>
                    
                    {/* Exclude Exempt Filter */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="filterExcludeExempt"
                        checked={filterExcludeExempt}
                        onCheckedChange={setFilterExcludeExempt}
                        data-testid="filter-exclude-exempt"
                      />
                      <label
                        htmlFor="filterExcludeExempt"
                        className="text-sm font-medium leading-none cursor-pointer whitespace-nowrap"
                      >
                        Exclude Exempt
                      </label>
                    </div>
                    
                    {(searchQuery || filterRole !== "all" || filterStatus !== "all" || filterExempt !== "all" || filterTeam !== "all" || filterManager !== "all" || filterNoTeams || filterTelnyxOnly || filterExcludeExempt) && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setSearchQuery("");
                          setFilterRole("all");
                          setFilterStatus("all");
                          setFilterExempt("all");
                          setFilterTeam("all");
                          setFilterManager("all");
                          setFilterNoTeams(false);
                          setFilterTelnyxOnly(false);
                          setFilterExcludeExempt(false);
                        }}
                      >
                        Clear filters
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full data-table">
                    <thead>
                      <tr>
                        <th className="w-10">
                          <Checkbox
                            checked={allFilteredSelected}
                            onCheckedChange={(checked) => checked ? selectAllUsers() : deselectAllUsers()}
                            data-testid="select-all-users"
                          />
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort("full_name")}
                        >
                          <div className="flex items-center">
                            Name <SortIcon column="full_name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort("email")}
                        >
                          <div className="flex items-center">
                            Email <SortIcon column="email" />
                          </div>
                        </th>
                        <th>Teams</th>
                        <th 
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort("role")}
                        >
                          <div className="flex items-center">
                            Role <SortIcon column="role" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort("is_exempt")}
                        >
                          <div className="flex items-center">
                            Exempt <SortIcon column="is_exempt" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort("is_active")}
                        >
                          <div className="flex items-center">
                            Status <SortIcon column="is_active" />
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-8 text-slate-500">
                            No users found matching your filters
                          </td>
                        </tr>
                      ) : (
                        paginatedUsers.map((user) => (
                          <tr key={user.id} data-testid={`user-row-${user.id}`}>
                            <td className="w-10">
                              <Checkbox
                                checked={selectedUserIds.has(user.id)}
                                onCheckedChange={() => toggleUserSelection(user.id)}
                                data-testid={`select-user-${user.id}`}
                              />
                            </td>
                            <td className="font-medium">
                              <button
                                onClick={() => openReportDialog(user)}
                                className="text-left text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                title="Click to generate log report"
                              >
                                {user.full_name}
                              </button>
                            </td>
                            <td>{user.email}</td>
                            <td>
                              {user.team_names && user.team_names.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {user.team_names.map((name, idx) => (
                                    <Badge key={idx} variant="outline" className="bg-slate-50 text-xs">
                                      {name}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td>
                              <Badge variant="outline" className={getRoleBadgeClass(user.role)}>
                                {user.role === "TEAM_LEADER" ? "Team Leader" : user.role}
                              </Badge>
                            </td>
                            <td>
                              {user.is_exempt ? (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                  Yes
                                </Badge>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td>
                              {user.is_active ? (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  Active
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                  Inactive
                                </Badge>
                              )}
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(user)}
                                data-testid={`edit-user-${user.id}`}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPermissionsDialog(user)}
                                data-testid={`permissions-user-${user.id}`}
                                title="Manage Permissions"
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openTelnyxDialog(user)}
                                data-testid={`telnyx-user-${user.id}`}
                                title={telnyxStatusMap[user.id]?.configured ? "Telnyx Configured - Click to Edit" : "Setup Telnyx"}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50 relative"
                              >
                                <Phone className="h-4 w-4" />
                                {telnyxStatusMap[user.id]?.configured && (
                                  <CheckCircle className="h-3 w-3 text-green-600 absolute -top-0.5 -right-0.5 bg-white rounded-full" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openLoginHistoryDialog(user)}
                                data-testid={`login-history-${user.id}`}
                                title="View Login History"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <History className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openActivityReportDialog(user)}
                                data-testid={`activity-report-${user.id}`}
                                title="Activity Report"
                                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResetUserPassword(user)}
                                data-testid={`reset-password-${user.id}`}
                                title="Reset Password"
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                              {isHrTeamMember && (
                                <HRDataHoverCard user={user} />
                              )}
                              {user.role !== "ADMIN" && (
                                <>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        data-testid={`delete-user-${user.id}`}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete {user.full_name}?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          This will permanently delete this user and all their logs. This action cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => handleDeleteUser(user.id)}
                                          className="bg-red-600 hover:bg-red-700"
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination Controls */}
                {filteredUsers.length > 0 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <span>Showing</span>
                      <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(parseInt(v))}>
                        <SelectTrigger className="w-[70px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5</SelectItem>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                      <span>of {filteredUsers.length} users</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-slate-600 px-2">
                        Page {currentPage} of {totalPages || 1}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage >= totalPages}
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage >= totalPages}
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Active Invites Tab */}
          <TabsContent value="invites" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Active Invites</CardTitle>
                <CardDescription>
                  Invite links that can still be used to sign up
                </CardDescription>
              </CardHeader>
              <CardContent>
                {activeInvites.length === 0 ? (
                  <div className="text-center py-12">
                    <LinkIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-600 mb-2">No Active Invites</h3>
                    <p className="text-slate-500 mb-4">Create an invite link to add new team members</p>
                    <Button onClick={() => setInviteDialogOpen(true)}>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Create Invite
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full data-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Email / Uses</th>
                          <th>Created</th>
                          <th>Expires</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeInvites.map((invite) => (
                          <tr key={invite.id} data-testid={`invite-row-${invite.id}`}>
                            <td>
                              {invite.is_reusable ? (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  Reusable
                                </Badge>
                              ) : (
                                <Badge variant="outline">Single-use</Badge>
                              )}
                            </td>
                            <td>
                              {invite.is_reusable ? (
                                <span className="text-slate-600">{invite.use_count || 0} signups</span>
                              ) : (
                                invite.intended_email || <span className="text-slate-400">Any email</span>
                              )}
                            </td>
                            <td className="text-slate-500">
                              {format(parseISO(invite.created_at), "MMM d, yyyy HH:mm")}
                            </td>
                            <td className="text-slate-500">
                              {format(parseISO(invite.expires_at), "MMM d, yyyy HH:mm")}
                            </td>
                            <td>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => handleCopyInviteLink(invite.setup_url)}
                                  data-testid={`copy-invite-${invite.id}`}
                                  title="Copy Link"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteInvite(invite.id)}
                                  data-testid={`delete-invite-${invite.id}`}
                                  title="Delete Link"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Roles Tab */}
          <TabsContent value="roles" className="mt-6">
            <Card>
              <CardContent className="pt-6">
                <RolesManagement />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-600" />
              Edit User
            </DialogTitle>
            {/* User Name Header */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 mt-2">
              <p className="text-lg font-semibold text-indigo-900">{selectedUser?.full_name}</p>
              <p className="text-sm text-indigo-600">{selectedUser?.email}</p>
            </div>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Default Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USER">User</SelectItem>
                  <SelectItem value="TEAM_LEADER">Team Leader</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                This is the user&apos;s default role. You can override it per team below.
              </p>
            </div>

            {/* Report To - Autocomplete Combobox */}
            <div className="space-y-2">
              <Label>Report To</Label>
              <Popover open={reportsToSearchOpen} onOpenChange={setReportsToSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={reportsToSearchOpen}
                    className="w-full justify-between"
                  >
                    {selectedReportsTo
                      ? users.find(u => u.id === selectedReportsTo)?.full_name || "Select manager..."
                      : "Select manager..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search users..." />
                    <CommandList>
                      <CommandEmpty>No user found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value=""
                          onSelect={() => {
                            setSelectedReportsTo("");
                            setReportsToSearchOpen(false);
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${selectedReportsTo === "" ? "opacity-100" : "opacity-0"}`} />
                          (None)
                        </CommandItem>
                        {users.filter(u => u.id !== selectedUser?.id).map((user) => (
                          <CommandItem
                            key={user.id}
                            value={user.full_name}
                            onSelect={() => {
                              setSelectedReportsTo(user.id);
                              setReportsToSearchOpen(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${selectedReportsTo === user.id ? "opacity-100" : "opacity-0"}`} />
                            {user.full_name}
                            <span className="ml-2 text-xs text-slate-500">({user.email})</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-slate-500">
                Select who this user reports to (optional)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Teams & Roles</Label>
              <p className="text-xs text-slate-500 mb-2">
                Select teams and assign a role for each team.
              </p>
              <div className="border rounded-md p-3 space-y-3 max-h-56 overflow-y-auto">
                {teams.length === 0 ? (
                  <p className="text-sm text-slate-400">No teams available. Create teams first.</p>
                ) : (
                  teams.map((team) => {
                    const isSelected = selectedTeamIds.includes(team.id);
                    return (
                      <div key={team.id} className={`flex items-center justify-between p-2 rounded-lg ${isSelected ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50'}`}>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`team-${team.id}`}
                            checked={isSelected}
                            onCheckedChange={() => handleTeamToggle(team.id)}
                            data-testid={`team-checkbox-${team.id}`}
                          />
                          <label
                            htmlFor={`team-${team.id}`}
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            {team.name}
                          </label>
                        </div>
                        {isSelected && (
                          <Select 
                            value={teamRoles[team.id] || 'USER'} 
                            onValueChange={(val) => handleTeamRoleChange(team.id, val)}
                          >
                            <SelectTrigger className="w-32 h-8 text-xs" data-testid={`team-role-${team.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="USER">User</SelectItem>
                              <SelectItem value="TEAM_LEADER">Team Leader</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              {selectedTeamIds.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedTeamIds.map((teamId) => {
                    const team = teams.find(t => t.id === teamId);
                    const role = teamRoles[teamId] || 'USER';
                    return team ? (
                      <Badge key={teamId} variant="secondary" className={`text-xs ${role === 'TEAM_LEADER' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100'}`}>
                        {team.name} ({role === 'TEAM_LEADER' ? 'Leader' : 'User'})
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
            
            {/* Exempt Checkbox */}
            <div className="flex items-center space-x-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <Checkbox
                id="isExempt"
                checked={selectedExempt}
                onCheckedChange={setSelectedExempt}
                data-testid="exempt-checkbox"
              />
              <div className="flex-1">
                <label
                  htmlFor="isExempt"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Exempt from Daily Logs
                </label>
                <p className="text-xs text-slate-500 mt-1">
                  This user won&apos;t be required to submit daily logs and won&apos;t receive reminders
                </p>
              </div>
            </div>
            
            {/* Form Assignment */}
            <div className="space-y-2">
              <Label>Assigned Form</Label>
              <Select value={selectedFormId || "default"} onValueChange={(val) => setSelectedFormId(val === "default" ? "" : val)}>
                <SelectTrigger data-testid="user-form-select">
                  <SelectValue placeholder="Default Form" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default Form</SelectItem>
                  {forms.map((form) => (
                    <SelectItem key={form.id} value={form.id}>
                      {form.name} {form.is_default && "(Default)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Select which daily log form this user should fill out
              </p>
            </div>
            
            {/* Danger Zone - Deactivate and Delete */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Account Actions</p>
              <div className="space-y-2">
                {/* Deactivate/Reactivate Button */}
                {selectedUser?.is_active !== false ? (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-amber-600 border-amber-200 hover:bg-amber-50 hover:text-amber-700"
                    onClick={handleDeactivateUser}
                    disabled={deactivating || selectedUser?.role === 'ADMIN'}
                    data-testid="deactivate-user-btn"
                  >
                    {deactivating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <UserX className="mr-2 h-4 w-4" />
                    )}
                    Deactivate User
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                    onClick={handleReactivateUser}
                    disabled={deactivating}
                    data-testid="reactivate-user-btn"
                  >
                    {deactivating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <UserCheck className="mr-2 h-4 w-4" />
                    )}
                    Reactivate User
                  </Button>
                )}
                <p className="text-xs text-slate-500 ml-1">
                  {selectedUser?.is_active !== false 
                    ? "Deactivating will disable login but preserve data for reports." 
                    : "This user is currently deactivated and cannot log in."}
                </p>
                
                {/* Delete Button */}
                <Button
                  variant="outline"
                  className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  onClick={openDeleteDialog}
                  disabled={selectedUser?.role === 'ADMIN'}
                  data-testid="delete-user-btn"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete User
                </Button>
                <p className="text-xs text-slate-500 ml-1">
                  Permanently delete this user and all their data. This cannot be undone.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveUserChanges} disabled={saving} data-testid="save-user-changes-button">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete User Permanently?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to delete <strong>{userToDelete?.full_name}</strong>? 
              </p>
              <p className="text-red-600 font-medium">
                This will permanently delete:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>User account and profile</li>
                <li>All daily logs submitted by this user</li>
                <li>All associated data from all tables</li>
              </ul>
              <p className="text-red-600 font-semibold mt-2">
                This action cannot be undone!
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteUser}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Permanently
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Report Days Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Generate Log Report
            </DialogTitle>
            <DialogDescription>
              Generate a log report for <strong>{reportUser?.full_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reportDays">Number of days</Label>
              <div className="flex gap-2">
                <Input
                  id="reportDays"
                  type="number"
                  min="1"
                  max="365"
                  value={reportDays}
                  onChange={(e) => setReportDays(e.target.value)}
                  placeholder="Enter days"
                  className="flex-1"
                />
                <Select value={reportDays} onValueChange={setReportDays}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Quick select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">Enter a custom number or use quick select</p>
            </div>
            
            <div className="space-y-2">
              <Label>Report Format</Label>
              <div className="flex gap-4">
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                  reportFormat === "column" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
                }`}>
                  <input
                    type="radio"
                    name="reportFormat"
                    value="column"
                    checked={reportFormat === "column"}
                    onChange={(e) => setReportFormat(e.target.value)}
                    className="sr-only"
                  />
                  <span className={`text-sm font-medium ${reportFormat === "column" ? "text-blue-700" : "text-slate-700"}`}>
                    Column Format
                  </span>
                </label>
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                  reportFormat === "row" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
                }`}>
                  <input
                    type="radio"
                    name="reportFormat"
                    value="row"
                    checked={reportFormat === "row"}
                    onChange={(e) => setReportFormat(e.target.value)}
                    className="sr-only"
                  />
                  <span className={`text-sm font-medium ${reportFormat === "row" ? "text-blue-700" : "text-slate-700"}`}>
                    Row Format
                  </span>
                </label>
              </div>
              <p className="text-xs text-slate-500">
                {reportFormat === "column" ? "Shows each field in a separate column" : "Shows each date with fields stacked vertically"}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={generateReport} disabled={!reportDays || parseInt(reportDays) < 1}>
              <FileText className="mr-2 h-4 w-4" />
              Generate Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Permissions Dialog */}
      <UserPermissionsDialog
        open={permissionsDialogOpen}
        onOpenChange={setPermissionsDialogOpen}
        user={permissionsUser}
        onUpdate={() => fetchData(true)}
      />

      {/* Telnyx Setup Dialog */}
      <Dialog open={telnyxDialogOpen} onOpenChange={setTelnyxDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-green-600" />
              Setup Telnyx for {telnyxUser?.full_name}
            </DialogTitle>
            <DialogDescription>
              Configure Telnyx API credentials for click-to-call and SMS functionality.
            </DialogDescription>
          </DialogHeader>
          
          {telnyxLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-green-600" />
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> API Key is configured globally in system settings. Only user-specific settings are shown below.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="telnyx-phone">Phone Number</Label>
                <Input
                  id="telnyx-phone"
                  type="tel"
                  placeholder="+1234567890"
                  value={telnyxSettings.phone_number}
                  onChange={(e) => setTelnyxSettings(s => ({ ...s, phone_number: e.target.value }))}
                  data-testid="telnyx-phone-input"
                />
                <p className="text-xs text-slate-500">Telnyx phone number for outbound calls/SMS</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="telnyx-connection">Connection ID (Call Control App ID)</Label>
                <Input
                  id="telnyx-connection"
                  placeholder="1234567890"
                  value={telnyxSettings.connection_id}
                  onChange={(e) => setTelnyxSettings(s => ({ ...s, connection_id: e.target.value }))}
                  data-testid="telnyx-connection-input"
                />
                <p className="text-xs text-slate-500">
                  From Telnyx Portal → Call Control → Applications → Application ID
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="telnyx-messaging">Messaging Profile ID (Optional)</Label>
                <Input
                  id="telnyx-messaging"
                  placeholder="xxxxx-xxxxx-xxxxx"
                  value={telnyxSettings.messaging_profile_id}
                  onChange={(e) => setTelnyxSettings(s => ({ ...s, messaging_profile_id: e.target.value }))}
                  data-testid="telnyx-messaging-input"
                />
                <p className="text-xs text-slate-500">For SMS messaging profile</p>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium text-slate-700 mb-3">WebRTC Credentials (Optional)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="telnyx-webrtc-user">WebRTC Username</Label>
                    <Input
                      id="telnyx-webrtc-user"
                      placeholder="Username"
                      value={telnyxSettings.webrtc_username}
                      onChange={(e) => setTelnyxSettings(s => ({ ...s, webrtc_username: e.target.value }))}
                      data-testid="telnyx-webrtc-user-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telnyx-webrtc-pass">WebRTC Password</Label>
                    <Input
                      id="telnyx-webrtc-pass"
                      type="password"
                      placeholder="Leave blank to keep existing"
                      value={telnyxSettings.webrtc_password}
                      onChange={(e) => setTelnyxSettings(s => ({ ...s, webrtc_password: e.target.value }))}
                      data-testid="telnyx-webrtc-pass-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">For browser-based calling</p>
              </div>
              
              {/* Test Connection Button */}
              <div className="border-t pt-4 mt-4">
                <Button 
                  variant="outline" 
                  onClick={handleTestConnection}
                  disabled={testingConnection || !telnyxSettings.phone_number || !telnyxSettings.connection_id}
                  className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="test-telnyx-connection-btn"
                >
                  {testingConnection ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Testing Connection...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Test Telnyx Configuration
                    </>
                  )}
                </Button>
                {(!telnyxSettings.phone_number || !telnyxSettings.connection_id) && (
                  <p className="text-xs text-amber-600 mt-1">Fill phone number and connection ID first</p>
                )}
              </div>
              
              {/* Test Connection Results */}
              {connectionTestResults && (
                <div className={`border rounded-lg p-4 ${connectionTestResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-start gap-3">
                    {connectionTestResults.success ? (
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h4 className={`font-semibold text-sm ${connectionTestResults.success ? 'text-green-900' : 'text-red-900'}`}>
                        {connectionTestResults.summary}
                      </h4>
                      
                      {/* Validation Checks */}
                      <div className="mt-3 space-y-2">
                        {connectionTestResults.checks?.map((check, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-xs">
                            {check.status === 'ok' ? (
                              <CheckCircle className="h-3 w-3 text-green-600 mt-0.5" />
                            ) : (
                              <X className="h-3 w-3 text-red-600 mt-0.5" />
                            )}
                            <div>
                              <span className="font-medium">{check.name}:</span>{' '}
                              <span className={check.status === 'ok' ? 'text-green-800' : 'text-red-800'}>
                                {check.message}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Instructions */}
                      {connectionTestResults.instructions && connectionTestResults.instructions.length > 0 && (
                        <div className="mt-3 p-2 bg-white rounded border border-red-300">
                          <h5 className="font-semibold text-xs text-red-900 mb-2">Action Required:</h5>
                          <ul className="list-disc list-inside space-y-1 text-xs text-red-800">
                            {connectionTestResults.instructions.map((instruction, idx) => (
                              <li key={idx}>{instruction}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Webhook Instructions */}
                      {connectionTestResults.webhook_instructions && (
                        <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-300">
                          <h5 className="font-semibold text-xs text-blue-900 mb-2 flex items-center gap-1">
                            <LinkIcon className="h-3 w-3" />
                            {connectionTestResults.webhook_instructions.title}
                          </h5>
                          <div className="space-y-0.5 text-xs text-blue-800">
                            {connectionTestResults.webhook_instructions.steps.map((step, idx) => (
                              <p key={idx} className="leading-relaxed">{step}</p>
                            ))}
                          </div>
                          {connectionTestResults.note && (
                            <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded">
                              <p className="text-xs font-semibold text-amber-900">{connectionTestResults.note}</p>
                            </div>
                          )}
                          <div className="mt-2">
                            <p className="text-xs font-semibold text-blue-900 mb-1">DEPLOYED Webhook URL (Use this):</p>
                            <div className="p-2 bg-white rounded font-mono text-xs break-all border">
                              {connectionTestResults.webhook_url}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Test Call Section */}
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-amber-600" />
                  Test Call
                </p>
                <div className="flex gap-2">
                  <Input
                    placeholder="+1234567890"
                    value={testCallNumber}
                    onChange={(e) => setTestCallNumber(e.target.value)}
                    className="flex-1"
                    data-testid="test-call-number"
                  />
                  <Button
                    variant="outline"
                    onClick={handleTestCall}
                    disabled={testingCall || !telnyxSettings.phone_number || !telnyxSettings.connection_id}
                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    data-testid="test-call-btn"
                  >
                    {testingCall ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Phone className="h-4 w-4 mr-1" />
                        Call
                      </>
                    )}
                  </Button>
                </div>
                {(!telnyxSettings.phone_number || !telnyxSettings.connection_id) && (
                  <p className="text-xs text-amber-600 mt-1">Save phone number and connection ID first</p>
                )}
              </div>
              
              {/* Test SMS Section */}
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  Test SMS
                </p>
                <div className="space-y-2">
                  <Input
                    placeholder="Phone number (+1234567890)"
                    value={testSmsNumber}
                    onChange={(e) => setTestSmsNumber(e.target.value)}
                    data-testid="test-sms-number"
                  />
                  <div className="flex gap-2">
                    <Input
                      placeholder="Test message..."
                      value={testSmsMessage}
                      onChange={(e) => setTestSmsMessage(e.target.value)}
                      className="flex-1"
                      data-testid="test-sms-message"
                    />
                    <Button
                      variant="outline"
                      onClick={handleTestSms}
                      disabled={testingSms || !telnyxSettings.phone_number}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid="test-sms-btn"
                    >
                      {testingSms ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Mail className="h-4 w-4 mr-1" />
                          Send
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                {!telnyxSettings.phone_number && (
                  <p className="text-xs text-blue-600 mt-1">Save phone number first</p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTelnyxDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTelnyxSettings} 
              disabled={telnyxSaving || telnyxLoading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-telnyx-btn"
            >
              {telnyxSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Phone className="mr-2 h-4 w-4" />
                  Save Telnyx Settings
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Merge Dialog */}
      <Dialog open={emailMergeDialogOpen} onOpenChange={setEmailMergeDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Send Email to Selected Users
            </DialogTitle>
            <DialogDescription>
              Compose an email to send to {selectedUserIds.size} selected user(s). 
              Use merge variables to personalize each email.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto space-y-4 py-4">
            {/* From Email Info */}
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">From:</span>
                <span className="text-sm text-slate-600">
                  {currentUser?.full_name || currentUser?.email || 'You'}
                </span>
                <span className="text-xs text-slate-400">
                  ({currentUser?.email})
                </span>
              </div>
            </div>

            {/* Merge Variables */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="text-sm font-medium text-blue-800 mb-2 block">
                Available Merge Variables (click to insert):
              </Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'full_name', label: 'Full Name' },
                  { key: 'first_name', label: 'First Name' },
                  { key: 'email', label: 'Email' },
                  { key: 'role', label: 'Role' },
                  { key: 'team_names', label: 'Team Names' }
                ].map(({ key, label }) => (
                  <Button
                    key={key}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="bg-white text-blue-700 border-blue-300 hover:bg-blue-100"
                    onClick={() => insertVariable(key)}
                  >
                    {`{{${key}}}`}
                    <span className="ml-1 text-xs text-slate-500">({label})</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="email-subject">Subject</Label>
              <Input
                id="email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject (can include merge variables)"
                data-testid="email-subject-input"
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label htmlFor="email-body">Message Body</Label>
              <textarea
                id="email-body"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Enter your message here...&#10;&#10;Use merge variables like {{full_name}} to personalize each email."
                className="w-full min-h-[200px] px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
                data-testid="email-body-input"
              />
            </div>

            {/* Preview */}
            {getSelectedUsers().length > 0 && emailBody && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Preview (first recipient: {getSelectedUsers()[0]?.full_name}):
                </Label>
                <div className="text-sm text-slate-600 whitespace-pre-wrap">
                  {emailBody
                    .replace(/\{\{full_name\}\}/g, getSelectedUsers()[0]?.full_name || '')
                    .replace(/\{\{first_name\}\}/g, (getSelectedUsers()[0]?.full_name || '').split(' ')[0])
                    .replace(/\{\{email\}\}/g, getSelectedUsers()[0]?.email || '')
                    .replace(/\{\{role\}\}/g, getSelectedUsers()[0]?.role || '')
                    .replace(/\{\{team_names\}\}/g, (getSelectedUsers()[0]?.team_names || []).join(', ') || 'No team')
                  }
                </div>
              </div>
            )}

            {/* Recipients List */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Recipients ({selectedUserIds.size}):</Label>
              <div className="max-h-32 overflow-auto p-2 bg-slate-50 rounded-md">
                <div className="flex flex-wrap gap-1">
                  {getSelectedUsers().map(user => (
                    <Badge key={user.id} variant="outline" className="text-xs">
                      {user.full_name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setEmailMergeDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendBulkEmails}
              disabled={sendingEmails || !emailSubject.trim() || !emailBody.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="send-bulk-email-btn"
            >
              {sendingEmails ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send to {selectedUserIds.size} User(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Login History Dialog */}
      <Dialog open={loginHistoryDialogOpen} onOpenChange={setLoginHistoryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Login History - {loginHistoryUser?.full_name}
            </DialogTitle>
            <DialogDescription>
              Recent login activity for this user (last 50 logins)
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : loginHistory.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <History className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p className="font-medium">No login history found for this user</p>
                <p className="text-sm mt-2">Login history is recorded when the user logs in from a web browser.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>IP Address</th>
                      <th>Browser</th>
                      <th>Operating System</th>
                      <th>Device Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginHistory.map((login) => (
                      <tr key={login.id}>
                        <td className="whitespace-nowrap">
                          {format(parseISO(login.logged_in_at), "MMM d, yyyy HH:mm:ss")}
                        </td>
                        <td className="font-mono text-sm">{login.ip_address}</td>
                        <td>
                          {login.browser_name && login.browser_version ? (
                            <span>
                              {login.browser_name} {login.browser_version}
                            </span>
                          ) : (
                            <span className="text-slate-400">Unknown</span>
                          )}
                        </td>
                        <td>
                          {login.os_name && login.os_version ? (
                            <span>
                              {login.os_name} {login.os_version}
                            </span>
                          ) : (
                            <span className="text-slate-400">Unknown</span>
                          )}
                        </td>
                        <td>
                          <Badge 
                            variant="outline" 
                            className={
                              login.device_type === "Mobile" 
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : login.device_type === "Tablet"
                                ? "bg-purple-50 text-purple-700 border-purple-200"
                                : "bg-slate-50 text-slate-700 border-slate-200"
                            }
                          >
                            {login.device_type || "Unknown"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoginHistoryDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Activity Report Dialog */}
      <Dialog open={activityReportDialogOpen} onOpenChange={setActivityReportDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              Activity Report - {activityReportUser?.full_name}
            </DialogTitle>
            <DialogDescription>
              Generate detailed or dashboard view of user activities
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {!activityReportData ? (
              <>
                {/* Configuration Form */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date Range</Label>
                    <Select value={activityDateRange} onValueChange={setActivityDateRange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="yesterday">Yesterday</SelectItem>
                        <SelectItem value="this_week">This Week</SelectItem>
                        <SelectItem value="last_week">Last Week</SelectItem>
                        <SelectItem value="this_month">This Month</SelectItem>
                        <SelectItem value="last_month">Last Month</SelectItem>
                        <SelectItem value="ytd">Year to Date</SelectItem>
                        <SelectItem value="custom">Custom Dates</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Report Type</Label>
                    <Select value={activityReportType} onValueChange={setActivityReportType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dashboard">Dashboard (Summary)</SelectItem>
                        <SelectItem value="detail">Detail (Full List)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {activityDateRange === 'custom' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={activityCustomStart}
                        onChange={(e) => setActivityCustomStart(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={activityCustomEnd}
                        onChange={(e) => setActivityCustomEnd(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Activity Types</Label>
                  <div className="grid grid-cols-3 gap-2 p-3 border rounded-lg bg-slate-50">
                    {['all', 'lead_created', 'lead_updated', 'lead_deleted', 'email_sent', 'sms_sent', 'call_logged', 'note_added', 'task_created'].map(type => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          id={`activity-${type}`}
                          checked={activityTypes.includes(type) || activityTypes.includes('all')}
                          onCheckedChange={(checked) => {
                            if (type === 'all') {
                              setActivityTypes(checked ? ['all'] : []);
                            } else {
                              if (checked) {
                                setActivityTypes(prev => prev.filter(t => t !== 'all').concat(type));
                              } else {
                                setActivityTypes(prev => prev.filter(t => t !== type && t !== 'all'));
                              }
                            }
                          }}
                          disabled={activityTypes.includes('all') && type !== 'all'}
                        />
                        <Label htmlFor={`activity-${type}`} className="text-sm font-normal cursor-pointer">
                          {type === 'all' ? 'ALL' : type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <Button onClick={handleGenerateActivityReport} disabled={loadingActivityReport} className="w-full">
                  {loadingActivityReport ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating Report...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      Generate Activity Report
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                {/* Report Display */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                      {activityReportType === 'dashboard' ? 'Activity Dashboard' : 'Detailed Activity Report'}
                    </h3>
                    <Button variant="outline" size="sm" onClick={() => setActivityReportData(null)}>
                      <X className="h-4 w-4 mr-1" />
                      New Report
                    </Button>
                  </div>

                  {activityReportType === 'dashboard' ? (
                    <DashboardReport data={activityReportData} />
                  ) : (
                    <DetailReport data={activityReportData} />
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* HR Data Dialog */}
      <Dialog open={hrDataDialogOpen} onOpenChange={setHrDataDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle className="h-5 w-5 text-teal-600" />
              HR Data - {hrDataUser?.full_name}
            </DialogTitle>
            <DialogDescription>
              Personal and employment information (HR team only)
            </DialogDescription>
          </DialogHeader>

          {loadingHrData ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Employment Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date_of_joining">Date of Joining</Label>
                    <Input
                      id="date_of_joining"
                      type="date"
                      value={hrData.date_of_joining || ''}
                      onChange={(e) => setHrData({...hrData, date_of_joining: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="work_location">OnSite or Remote</Label>
                    <Select value={hrData.work_location || ''} onValueChange={(val) => setHrData({...hrData, work_location: val})}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="OnSite">OnSite</SelectItem>
                        <SelectItem value="Remote">Remote</SelectItem>
                        <SelectItem value="Hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Compensation</h3>
                
                {/* Currency Selection */}
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="currency"
                        value="INR"
                        checked={(hrData.currency || 'INR') === 'INR'}
                        onChange={(e) => setHrData({...hrData, currency: e.target.value})}
                        className="h-4 w-4 text-teal-600"
                      />
                      <span className="text-sm">INR (₹)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="currency"
                        value="USD"
                        checked={hrData.currency === 'USD'}
                        onChange={(e) => setHrData({...hrData, currency: e.target.value})}
                        className="h-4 w-4 text-teal-600"
                      />
                      <span className="text-sm">USD ($)</span>
                    </label>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_annual_ctc">Start Annual CTC</Label>
                    <Input
                      id="start_annual_ctc"
                      type="text"
                      value={hrData.start_annual_ctc || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^\d]/g, '');
                        const maxVal = (hrData.currency || 'INR') === 'INR' ? 9999999 : 9999999;
                        if (val === '' || (parseInt(val) <= maxVal)) {
                          setHrData({...hrData, start_annual_ctc: val ? parseInt(val) : null});
                        }
                      }}
                      placeholder={(hrData.currency || 'INR') === 'INR' ? '₹ 99,99,999' : '$ 9,999,999'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="current_annual_ctc">Current Annual CTC</Label>
                    <Input
                      id="current_annual_ctc"
                      type="text"
                      value={hrData.current_annual_ctc || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^\d]/g, '');
                        const maxVal = (hrData.currency || 'INR') === 'INR' ? 9999999 : 9999999;
                        if (val === '' || (parseInt(val) <= maxVal)) {
                          setHrData({...hrData, current_annual_ctc: val ? parseInt(val) : null});
                        }
                      }}
                      placeholder={(hrData.currency || 'INR') === 'INR' ? '₹ 99,99,999' : '$ 9,999,999'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_appraisal_date">Last Appraisal Date</Label>
                    <Input
                      id="last_appraisal_date"
                      type="date"
                      value={hrData.last_appraisal_date || ''}
                      onChange={(e) => setHrData({...hrData, last_appraisal_date: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Personal Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={hrData.date_of_birth || ''}
                      onChange={(e) => setHrData({...hrData, date_of_birth: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="marital_status">Marital Status</Label>
                    <Select value={hrData.marital_status || ''} onValueChange={(val) => setHrData({...hrData, marital_status: val})}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Single">Single</SelectItem>
                        <SelectItem value="Married">Married</SelectItem>
                        <SelectItem value="Divorced">Divorced</SelectItem>
                        <SelectItem value="Widowed">Widowed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_anniversary">Date of Anniversary</Label>
                    <Input
                      id="date_of_anniversary"
                      type="date"
                      value={hrData.date_of_anniversary || ''}
                      onChange={(e) => setHrData({...hrData, date_of_anniversary: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hometown">HomeTown (where parents live)</Label>
                    <Input
                      id="hometown"
                      type="text"
                      value={hrData.hometown || ''}
                      onChange={(e) => setHrData({...hrData, hometown: e.target.value})}
                      placeholder="City/Town"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hobbies">Hobbies</Label>
                  <Textarea
                    id="hobbies"
                    value={hrData.hobbies || ''}
                    onChange={(e) => setHrData({...hrData, hobbies: e.target.value})}
                    placeholder="List hobbies and interests..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setHrDataDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveHrData} disabled={savingHrData || loadingHrData} className="bg-teal-600 hover:bg-teal-700">
              {savingHrData ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : 'Save HR Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </div>
  );
}
